import { Modal } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { cloneDeep, uniqueId } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import { TextField } from "../../../commons/form-fields/TextField.js";

type FormValues = {
  group: string;
};

interface Props {
  values: FormValues;
  disabled?: boolean;
  handleClose: () => void;
  handleComplete: (values: FormValues) => void | Promise<void>;
}

export const GroupModal = ({
  handleClose,
  handleComplete,
  values,
  disabled,
}: Props) => {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const initialValues = React.useMemo(() => cloneDeep(values), [values]);

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        group: Yup.string().label(t("Group", { ns: "QuoteItem" })),
      }),
    [t]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  const handleSubmit = async (values: FormValues) => {
    await handleComplete(values);
    handleClose();
  };

  return (
    <Formik<FormValues>
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
      initialValues={initialValues}
    >
      {formikProps => (
        <Modal
          title={t("Group", { ns: "QuoteItem" })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: formikProps.isSubmitting,
              },
            },
            {
              label: t("Save", { ns: "Global" }),
              buttonProps: {
                form: formId,
                type: "submit",
                loading: formikProps.isSubmitting,
                disabled: !formikProps.isValid || !formikProps.dirty,
              },
            },
          ]}
        >
          <Form id={formId}>
            <TextField
              autoFocus
              name={"group"}
              label={t("Group", { ns: "QuoteItem" })}
              disabled={disabled ?? formikProps.isSubmitting}
            />
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export const defaultGroupValues: FormValues = {
  group: "",
};
